import AppGlobalsContext from "app/AppGlobalsContext";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import IconDeparting from "app/pages/.shared/IconDeparting";
import IconLocation from "app/pages/.shared/IconLocation";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ClearIndicatorSdpSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/ClearIndicatorSdpSearchInput";
import DestinationInputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/DestinationInputValueFormat";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import LocationMenuChip from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuChip";
import SmartDPDateCalendarContainer from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPDateCalendarContainer";
import {
	defaultValues,
	validateDepartureCity,
	validateDestination,
	validateSearch,
} from "app/pages/SmartDP/Search/SDPSearchForm/smartDPSearchFormSchema";
import SmartDPSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchInput";
import TravellersRoomButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomButton";
import { destinationResortFilterOptions, isServerSide, sortOptions } from "app/utils/utils";
import classNames from "classnames";
import { differenceInDays, format } from "date-fns";
import { Form, Formik } from "formik";
import { isEmpty, orderBy } from "lodash";
import get from "lodash/get";
import PropTypes from "prop-types";
import { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import {
	DESTINATION_USA_CODE,
	FLAGSHIP_EVENT_KPIS,
	FS_QUOTE_EVENT_NAME,
	SDP_ROTATION_DATE_FORMAT,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import CalendarDisabledView from "app/pages/SmartDP/Search/SDPSearchForm/CalendarDisabledView";
import TravellersRoomDrawerButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomDrawerButton";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { brandPropTypes } from "app/utils/propTypes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import InputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/InputValueFormat";
import { smartDPDestinationSuggestion } from "app/utils/smartDPUtils";
import IconSearchFormDropdown from "app/pages/.shared/static/icons/IconSearchFormDropdown";
import TopOptionChip from "app/pages/SmartDP/Search/SDPSearchForm/TopOptionChip/TopOptionChip";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "app/pages/.shared/form/SearchForm.scss";
import "./SmartDPSearchForm.scss";
import LastSearchContainer from "app/features/SearchEngine/LastSearch/LastSearchContainer";

const SmartDPSearchForm = ({
	onSuccess = () => {},
	departureCities = [],
	destinations = [],
	cabins = [],
	initialValues = {},
	topDestinationsList = [],
	validateOnMount,
	usePortalForMenu,
	displayLoader,
	errorScrollIntoView = true,
	onFieldFocus = () => {},
	isAutoScrollActive,
	resetAllSDPProductsFilter = () => {},
}) => {
	const { sendHits } = useFlagship();
	const { track } = useAnalytics();

	const selectRefs = {
		departureCity: useRef(null),
		destinationResort: useRef(null),
		departureDate: useRef(null),
		cabin: useRef(null),
	};

	const [isTopDepartureCitiesMenuDisplayed, setIsTopDepartureCitiesMenuDisplayed] = useState(
		true
	);
	const [isTopDestinationsMenuDisplayed, setIsTopDestinationsMenuDisplayed] = useState(true);

	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const handleSubmit = useCallback(
		(values, actions) => {
			resetAllSDPProductsFilter();
			actions.setSubmitting(false);
			onSuccess({ values, pathname });
		},
		[departureCities, destinations, cabins, pathname]
	);
	const [destinationInput, setDestinationInput] = useState();
	// fix pour ne pas avoir les labels sur 2 lignes au premier rendu
	const mergeInitialValues = isServerSide
		? {
				occupancies: [{ adults: 2, children: 0, childrenBirthdates: [] }],
		  }
		: {
				...defaultValues,
				...initialValues,
		  };

	const isListingOrQuote = pathname.includes("/sdp/listing") || pathname === "/sdp/booking/quote";

	const isSticky =
		!isMobile && ["/sdp/listing", "/sdp/booking/quote"].some(path => pathname.includes(path));

	const hasCabins = isServerSide || cabins.length > 0;

	const topDepartureCities = orderBy(
		departureCities.filter(option => option?.isMain),
		["mainOrder"],
		["asc"]
	);

	const getLocationSelectionEventName = useCallback(
		(type, isTopSelection) => `form_click_${type}_${isTopSelection ? "popular" : "manual"}`,
		[]
	);

	const onHandleClickSearchButton = useCallback(() => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_QUOTE_EVENT_NAME.CLICK_ON_SEARCH_CTA,
		});
	}, []);

	const [refs, setRefs] = useState(null);

	const updateFloatingPositionReference = useCallback(refs => {
		setRefs(refs);
	}, []);

	const customFieldIds = ["departureDate"];

	const fieldIds = useMemo(() => ["departureCity", "destinationResort", "departureDate"], []);

	const fieldOrder = useMemo(() => fieldIds.map(id => ({ id, ref: selectRefs[id] })), [
		fieldIds,
		selectRefs,
	]);
	const getFieldValue = (values, fieldId) => {
		const nestedFields = {
			departureDate: ["travelDates", "departureDate"],
		};

		if (nestedFields[fieldId]) {
			return nestedFields[fieldId].reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				values
			);
		}

		return values[fieldId];
	};

	const handleFieldChange = useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (value) {
				const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
				if (currentIndex === -1) {
					return;
				}

				const previousFields = fieldOrder.slice(0, currentIndex);
				const firstEmptyPrevField = previousFields.find(
					field => !getFieldValue(values, field.id)
				);

				if (firstEmptyPrevField && firstEmptyPrevField.ref.current) {
					if (customFieldIds.includes(firstEmptyPrevField.id)) {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.click();
						});
					} else {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.focus();
						});
					}
					return;
				}

				let nextIndex = currentIndex + 1;

				while (nextIndex < fieldOrder.length) {
					const nextField = fieldOrder[nextIndex];
					if (!getFieldValue(values, nextField.id) && nextField.ref.current) {
						requestAnimationFrame(() => {
							const event = new MouseEvent("click", {
								view: window,
								bubbles: true,
								cancelable: true,
							});
							nextField.ref.current.dispatchEvent(event);
							nextField.ref.current.click();
						});
						break;
					}
					nextIndex++;
				}
			}
		},
		[fieldOrder, customFieldIds]
	);

	return (
		<div
			className={classNames("search-form search-form--sdp", {
				"search-form--sdp-with-cabins": hasCabins,
			})}
			ref={refs?.setPositionReference}
		>
			<Formik
				enableReinitialize
				initialValues={mergeInitialValues}
				validate={validateSearch(false)}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={
					validateOnMount && {
						departureCity: true,
						destinationResort: true,
						travelDates: {
							departureDate: true,
							endDate: true,
						},
						duration: {
							code: true,
						},
						occupancies: true,
						cabin: true,
					}
				}
				validateOnMount={validateOnMount}
			>
				{({ values, errors, isValid, isSubmitting, setFieldValue, submitCount }) => {
					const handleTravellersConfirmation = values => {
						setFieldValue("occupancies", values);
					};

					const sdpRotationUri = values?.destinationResort?.departureCities?.find(
						city => city?.code === values?.departureCity?.code
					)?.uri;
					const hasLocationSelection = Boolean(
						values?.departureCity && values?.destinationResort
					);

					const isUsaDestination = DESTINATION_USA_CODE.includes(
						values?.destinationResort?.code
					);

					return (
						<>
							<Form
								className="search-form__form search-form--sdp"
								data-testid="sdp-search-form"
							>
								<SmartDPSearchInput
									id="departureCity"
									name="departureCity"
									usePortalForMenu={usePortalForMenu}
									selectRef={selectRefs.departureCity}
									openMenuOnFocus={
										isMobile
											? true
											: !values.departureCity && topDepartureCities.length > 0
									}
									openMenuOnClick={
										isMobile
											? true
											: !values.departureCity && topDepartureCities.length > 0
									}
									onChange={value => {
										if (value) {
											track(
												getLocationSelectionEventName(
													"departure_city",
													isTopDepartureCitiesMenuDisplayed
												),
												{
													locationLabel: value?.label,
												}
											);
										}
										handleFieldChange(
											"departureCity",
											setFieldValue,
											value,
											values
										);
									}}
									className="search-form__field search-form__field-departure"
									data-testid="departure-city-input"
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									validate={validateDepartureCity}
									label={
										<FormattedMessage id="sdp.search.departure.city.input.label" />
									}
									drawerInputLabel={
										<FormattedMessage id="sdp.search.departure.city.input.label" />
									}
									drawerTitleLabel={
										<FormattedMessage id="drawer.departure.title" />
									}
									components={{
										SingleValue: InputValueFormat,
										MenuList: isTopDepartureCitiesMenuDisplayed
											? LocationMenuChip
											: LocationMenuList,
										ClearIndicator: ClearIndicatorSdpSearchInput,
									}}
									formatOptionLabel={({ label = "" }) => {
										if (isTopDepartureCitiesMenuDisplayed) {
											return <TopOptionChip label={label} />;
										}

										return (
											<div className="search-form__suggestion">
												<div className="search-form__suggestion-picto">
													<IconDeparting />
												</div>
												<Typography
													className="search-form__suggestion-first-label"
													variant={TYPOGRAPHY_VARIANTS.REGULAR}
												>
													<span
														dangerouslySetInnerHTML={{
															__html: label,
														}}
													/>
												</Typography>
											</div>
										);
									}}
									getOptionValue={({ code }) => code}
									getOptionLabel={({ label }) => label}
									options={
										isTopDepartureCitiesMenuDisplayed
											? topDepartureCities
											: departureCities
									}
									// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
									// valeur par defaut
									value={values?.departureCity || ""}
									noOptionsMessage={() => {
										return <FormattedMessage id="sdp.search.no.result.label" />;
									}}
									popperWidth={360}
									popperOffset={[-16, 2]}
									locationMenuLabel={
										<FormattedMessage id="sdp.search.top.departure.cities.label" />
									}
									isTopLocationMenuDisplayed={isTopDepartureCitiesMenuDisplayed}
									onInputChange={inputValue => {
										if (isEmpty(inputValue)) {
											setIsTopDepartureCitiesMenuDisplayed(
												topDepartureCities.length > 0
											);
										} else if (isTopDepartureCitiesMenuDisplayed) {
											setIsTopDepartureCitiesMenuDisplayed(false);
										}
									}}
									loading={displayLoader}
									isFloating
									floatingConfig={{
										placement: "bottom-start",
										offset: ({ rects }) => ({
											mainAxis: -rects.reference.height - 16,
											crossAxis: -16,
										}),
									}}
									placeholder={
										<FormattedMessage id="sdp.search.departure.city.input.label.mobile" />
									}
									showPlaceholderOnlyOnFocus={true}
								/>

								<SmartDPSearchInput
									id="destinationResort"
									name="destinationResort"
									usePortalForMenu={usePortalForMenu}
									selectRef={selectRefs.destinationResort}
									openMenuOnFocus={
										isMobile
											? true
											: !values.destinationResort &&
											  topDestinationsList.length > 0
									}
									openMenuOnClick={
										isMobile
											? true
											: !values.destinationResort &&
											  topDestinationsList.length > 0
									}
									validate={validateDestination}
									onChange={value => {
										handleFieldChange(
											"destinationResort",
											setFieldValue,
											value,
											values
										);

										if (value) {
											track(
												getLocationSelectionEventName(
													"destination",
													isTopDestinationsMenuDisplayed
												),
												{
													locationLabel:
														value?.label ||
														value?.labels?.slice(-1).join(", "),
												}
											);
										}

										if (value?.label) {
											sendHits({
												type: HitType.EVENT,
												category: EventCategory.ACTION_TRACKING,
												action:
													FLAGSHIP_EVENT_KPIS.FLAG_SDP_FUNNEL_FLAGS_TRIGGERED,
												label: value?.label,
											});
										}
									}}
									className="search-form__field search-form__field-destination"
									data-testid="destination-resort-input"
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									filterOption={
										isTopDestinationsMenuDisplayed
											? undefined
											: destinationResortFilterOptions
									}
									label={
										<FormattedMessage id="sdp.search.destination.input.label" />
									}
									drawerInputLabel={
										<FormattedMessage id="sdp.search.destination.input.label" />
									}
									formatOptionLabel={({ labels = [], levels }) => {
										const firstSuggestion = labels?.slice(-1).join(", ");
										const secondSuggestion = smartDPDestinationSuggestion(
											firstSuggestion,
											levels
										);
										const fullLabel = [firstSuggestion, secondSuggestion]
											.filter(Boolean)
											.join(", ");

										if (isTopDestinationsMenuDisplayed) {
											return <TopOptionChip label={fullLabel} />;
										}

										return (
											<div className="search-form__suggestion">
												<div className="search-form__suggestion-picto">
													<IconLocation style={{ marginLeft: "2px" }} />
												</div>
												<div className="search-form__suggestion-text">
													<Typography
														className="search-form__suggestion-first-label"
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
													>
														<span
															dangerouslySetInnerHTML={{
																__html: firstSuggestion,
															}}
														/>
													</Typography>
													{secondSuggestion && (
														<span
															className="search-form__suggestion-second-label"
															dangerouslySetInnerHTML={{
																__html: secondSuggestion,
															}}
														/>
													)}
												</div>
											</div>
										);
									}}
									components={{
										SingleValue: DestinationInputValueFormat,
										ClearIndicator: ClearIndicatorSdpSearchInput,
										MenuList: isTopDestinationsMenuDisplayed
											? LocationMenuChip
											: LocationMenuList,
									}}
									getOptionValue={({ code }) => code}
									getOptionLabel={({ label }) => label}
									options={
										isTopDestinationsMenuDisplayed
											? topDestinationsList
											: sortOptions(destinations, destinationInput)
									}
									// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
									// valeur par defaut
									value={values?.destinationResort || ""}
									noOptionsMessage={() => {
										return <FormattedMessage id="sdp.search.no.result.label" />;
									}}
									popperWidth={360}
									popperOffset={[-16, 2]}
									locationMenuLabel={
										<FormattedMessage id="sdp.search.top.destinations.cities.label" />
									}
									isTopLocationMenuDisplayed={isTopDestinationsMenuDisplayed}
									onInputChange={inputValue => {
										if (isEmpty(inputValue)) {
											setIsTopDestinationsMenuDisplayed(
												topDestinationsList.length > 0
											);
										} else if (isTopDestinationsMenuDisplayed) {
											setIsTopDestinationsMenuDisplayed(false);
										}
										setDestinationInput(inputValue);
									}}
									loading={displayLoader}
									isFloating
									floatingConfig={{
										placement: "bottom-start",
										offset: ({ rects }) => ({
											mainAxis: -rects.reference.height - 16,
											crossAxis: -16,
										}),
									}}
									placeholder={
										<FormattedMessage id="sdp.search.destination.input.label.mobile" />
									}
									showPlaceholderOnlyOnFocus={true}
								/>

								<SmartDPDateCalendarContainer
									id="travelDates"
									name="travelDates"
									data-testid="travel-date-input"
									selectRef={selectRefs.departureDate}
									hasLocationSelection={hasLocationSelection}
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field search-form__field-calendar"
									updateFloatingPositionReference={
										updateFloatingPositionReference
									}
									popperOffset={[0, -58]}
									calendarPopoverWidth={isListingOrQuote ? 998 : 1024} // 1024 - 32 padding
									calendarDisabledView={
										<CalendarDisabledView
											mainMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="sdp.calendar.unavailable.date.label" />
												) : (
													<FormattedMessage id="sdp.calendar.no.destination.no.departure.city.label" />
												)
											}
											secondaryMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="sdp.calendar.unavailable.date.sublabel" />
												) : null
											}
										/>
									}
									uri={sdpRotationUri}
									handleDayChange={({
										durationsOfSelectedDepartureDate = [],
										selectedEndDate,
										selectedDepartureDate,
									}) => {
										// durationsOfSelectedDepartureDate [{duration, ed}]

										if (isEmpty(durationsOfSelectedDepartureDate)) {
											if (selectedEndDate && selectedDepartureDate) {
												const daysDifference = differenceInDays(
													new Date(selectedEndDate),
													new Date(selectedDepartureDate)
												);
												setFieldValue("duration", {
													code: daysDifference,
												});
											} else {
												setFieldValue("duration", {
													code: "",
												});
											}
										} else if (selectedEndDate) {
											const formattedSelectedEndDate = format(
												new Date(selectedEndDate),
												SDP_ROTATION_DATE_FORMAT
											);

											const dataOfSelectedDepartureDate = durationsOfSelectedDepartureDate.find(
												data => data.ed === formattedSelectedEndDate
											);
											if (dataOfSelectedDepartureDate) {
												setFieldValue("duration", {
													code: dataOfSelectedDepartureDate.duration,
												});
											} else {
												setFieldValue("duration", {
													code: "",
												});
											}
										} else {
											setFieldValue("duration", {
												code: "",
											});
										}
									}}
									loading={displayLoader}
								/>

								{isMobile ? (
									<TravellersRoomDrawerButton
										className={classNames(
											"search-form__field search-form__field-rooms",
											{
												"search-form__field--fullsize": !hasCabins,
											}
										)}
										id="travellersRooms"
										onConfirmation={handleTravellersConfirmation}
										initialValues={values.occupancies}
										label={
											<FormattedMessage id="sdp.search.travellers.input.label" />
										}
										validateOnMount={validateOnMount}
										warningMessage={isUsaDestination}
									/>
								) : (
									<TravellersRoomButton
										className={classNames(
											"search-form__field search-form__field-rooms",
											{
												"search-form__field--fullsize": !hasCabins,
											}
										)}
										id="travellersRooms"
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										onConfirmation={handleTravellersConfirmation}
										initialValues={values.occupancies}
										label={
											<FormattedMessage id="sdp.search.travellers.input.label" />
										}
										stickyMode={isSticky}
										validateOnMount={validateOnMount}
										loading={displayLoader}
										popperWidth={360}
										popperOffset={[80, 4]}
										warningMessage={isUsaDestination}
									/>
								)}

								{hasCabins && (
									<SmartDPSearchInput
										id="cabin"
										name="cabin"
										selectRef={selectRefs.cabin}
										openMenuOnFocus
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										className="search-form__field search-form__field-cabin"
										data-testid="cabin-input"
										isSearchable={false}
										{...isMobile && {
											label: (
												<FormattedMessage id="sdp.search.cabin.input.label" />
											),
										}}
										getOptionLabel={({ label = "", suffix = "" }) =>
											`${label}${suffix ? `, ${suffix}` : ""}`
										}
										components={{
											SingleValue: InputValueFormat,
											ClearIndicator: ClearIndicatorSdpSearchInput,
										}}
										getOptionValue={({ code }) => code}
										options={cabins}
										value={cabins.find(({ code }) => {
											return code === get(values, "cabin.code");
										})}
										popperWidth={240}
										popperOffset={[-10, -40]}
										loading={displayLoader}
										{...!isMobile && {
											inputValueIcon: (
												<IconSearchFormDropdown width={16} height={16} />
											),
										}}
									/>
								)}

								{/* Do not display error message on validationOnMount (redirection from emirates) */}
								{!isValid && submitCount >= 1 && (
									<div className="search-form__error">
										<FormErrorMessages
											errors={errors}
											isSubmitting={isSubmitting}
											submitCount={submitCount}
											disableErrorsDetails={true}
											message={
												<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
													<FormattedMessage id="sdp.search.form.error.message" />
												</Typography>
											}
											scrollInToView={errorScrollIntoView}
										/>
									</div>
								)}

								<div className="search-form__footer">
									<Button
										variant={BUTTON_VARIANTS.FILLED}
										size={BUTTON_SIZES.LARGE}
										label={
											<FormattedMessage
												id={
													isListingOrQuote
														? "sdp.search.refresh.label"
														: "sdp.search.cta.label"
												}
											/>
										}
										submit
										className="search-form__button"
										loading={isSubmitting}
										data-testid="search-form-button"
										onClick={onHandleClickSearchButton}
										disabled={displayLoader || isSubmitting}
									/>

									<div className="search-form__last-search">
										<LastSearchContainer
											websiteType={WEBSITE_TUNNEL_TYPES.HOLIDAYS}
											updateFloatingPositionReference={
												updateFloatingPositionReference
											}
											onFocus={onFieldFocus}
										/>
									</div>
								</div>
								<div className="search-form__first-line-break" />
							</Form>
						</>
					);
				}}
			</Formik>
		</div>
	);
};

SmartDPSearchForm.propTypes = {
	onSuccess: PropTypes.func,
	departureCities: PropTypes.array,
	destinations: PropTypes.array,
	cabins: PropTypes.array,
	initialValues: PropTypes.object,
	topDestinationsList: PropTypes.array,
	usePortalForMenu: PropTypes.bool,
	validateOnMount: PropTypes.bool,
	handleFormError: PropTypes.func,
	onFieldFocus: PropTypes.func,
	isAutoScrollActive: PropTypes.bool,
	displayLoader: PropTypes.bool,
	brand: brandPropTypes,
	errorScrollIntoView: PropTypes.bool,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default memo(SmartDPSearchForm);
